"use client";
import React from "react";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { BLOCKS, INLINES } from "@contentful/rich-text-types";
import Image from "next/image"; // Si tu utilises Next.js pour l'optimisation des images
import Link from "next/link";
import { stringToSlug } from "@/utils/slug";
import { ArrowRight, Dot, MoveDown } from "lucide-react";
import YouTubePlayer from "../video/player";
import { ProfileSmallCard } from "../profile/smallCard";
import { Timeline } from "../calendar/timeline";
// import { YoutubeVideoIframe } from "../player/youtube";

interface RichTextProps {
  richTextDocument: any; // Typiquement un JSON venant de Contentful
  links: any;
  keyIndicators: string[];
}

export const RichTextOperation: React.FC<RichTextProps> = ({
  richTextDocument,
  links,
  keyIndicators,
}) => {
  // console.log("links", links);

  const findEntryDetails = (entryId: string) => {
    // console.log("entryId", entryId);
    // console.log("links", links);
    return links?.entries?.hyperlink.find(
      (entry: any) => entry.sys.id === entryId
    );
  };

  const findBlockDetails = (id: string) => {
    // console.log("links.entries.block", links.entries.block, id);
    return links.entries.block.find((block: any) => block.sys.id === id);
  };

  const findAssetDetails = (assetId: string) => {
    return links.assets.block.find((asset: any) => asset.sys.id === assetId);
  };

  const options = {
    renderNode: {
      [BLOCKS.PARAGRAPH]: (node: any, children: any) => {
        const text = children.join("") || "";

        // TALLY FORM SHORTCODE
        const keyNumbersShortcodeRegex = /\[KeyNumbers\]/;
        const matchKeyNumbers = keyNumbersShortcodeRegex.exec(text);
        if (matchKeyNumbers) {
          return (
            <div className="bg-brand-green bg-line-pattern -ml-6 xl:-ml-16 p-6 py-12 xl:p-16 my-10 white space-y-4 text-white">
              <h4 className="text-2xl">Chiffres clés</h4>
              <div className="space-y-2 text-white/80 text-lg">
                {keyIndicators?.map((keyIndicator: string) => (
                  <div
                    key={keyIndicator}
                    className="flex items-start xl:items-center "
                  >
                    <ArrowRight size={24} className="mr-2 pt-1.5 xl:pt-0" />
                    <p>{keyIndicator}</p>
                  </div>
                ))}
              </div>
            </div>
          );
        }
        // YOUTUBE PLAYER SHORTCODE
        const youtubeShortcodeRegex = /\[YoutubePlayer youtubeId="(.+?)" \/\]/;
        const matchYoutube = youtubeShortcodeRegex.exec(text);

        if (matchYoutube) {
          // const youtubeId = matchYoutube[1]; // Capture la valeur de tallyId
          return (
            <div className="my-4">
              {/* <TallyFormButton tallyId={tallyId} anchorText={anchorText} /> */}
              {/* <YoutubeVideoIframe videoId={youtubeId} title={"Player"} /> */}
            </div>
          );
        }

        // Si aucun shortcode n'est trouvé, retourne le contenu par défaut
        return <p className="text-md font-light mb-6">{children}</p>;
      },
      [BLOCKS.HEADING_1]: (node: any, children: any) => (
        <h1 className="text-3xl uppercase font-bold mb-4">{children}</h1>
      ),
      [BLOCKS.HEADING_2]: (node: any, children: any) => (
        <div className="relative first:mt-0 mt-10">
          <div className="flex flex-col absolute top-3 -left-20 items-center">
            <Dot size={32} className="text-brand-red" />
            <MoveDown size={32} strokeWidth={1.3} />
          </div>
          <h2
            id={stringToSlug(children[0])}
            className="text-3xl text-brand-blue font-bold mb-4"
          >
            {children}
          </h2>
        </div>
      ),
      [BLOCKS.HEADING_3]: (node: any, children: any) => (
        <h3 className="text-lg font-bold mb-4">{children}</h3>
      ),
      [BLOCKS.UL_LIST]: (node: any, children: any) => (
        <ul style={{ listStyleType: "circle" }} className="ml-5 mb-4">
          {children}
        </ul>
      ),
      [BLOCKS.OL_LIST]: (node: any, children: any) => (
        <ol className="list-decimal ml-5 mb-4">{children}</ol>
      ),
      [BLOCKS.QUOTE]: (node: any, children: any) => (
        <blockquote className="border-l-4 border-gray-500 pl-4 italic mb-4">
          {children}
        </blockquote>
      ),
      [BLOCKS.EMBEDDED_ASSET]: (node: any) => {
        const assetId = node.data.target.sys.id;
        const asset = findAssetDetails(assetId);

        // return <pre>{JSON.stringify(asset, null, 2)}</pre>;

        // const imageUrl = file.url;
        // return <pre>{JSON.stringify(asset, null, 2)}</pre>;
        return asset.contentType.indexOf("image") !== -1 ? (
          <div className="my-6">
            <Image
              src={`${asset.url}`}
              alt={asset.title || "Embedded Image"}
              width={800}
              height={400}
              className="mx-auto w-full"
            />
          </div>
        ) : asset.contentType.indexOf("pdf") !== -1 ? (
          <a
            href={asset.url}
            target="_blank"
            className="flex px-3 py-1.5 justify-between text-sm rounded-sm shadow mb-4"
          >
            <span>{asset.title}</span>
            <span className="text-brand-red text-right">
              Télécharger (.pdf)
            </span>
          </a>
        ) : null;
      },

      [INLINES.EMBEDDED_ENTRY]: (node: any) => {
        const entryId = node.data.target.sys.id;
        const entry = findEntryDetails(entryId);
        console.log("entry", entry);
        return <div>test 2</div>;
      },
      [INLINES.ENTRY_HYPERLINK]: (node: any, children: any) => {
        const entryId = node.data.target.sys.id;
        const entry = findEntryDetails(entryId);

        // console.log("entry", entry);

        const link = () => {
          switch (entry.__typename) {
            case "Post":
              return `/posts/${entry.sys.id}`;
            case "Page":
              return `/page/${entry.fields.slug}`;
            case "Organisation":
              return `/the-consortium/${entry.fields.slug}`;
            default:
              return "/";
          }
        };

        if (!entry) {
          return <span>{children}</span>; // Si l'entrée n'existe pas, afficher le texte sans lien
        }

        // // Générer l'URL en fonction du contentType et du slug
        // const contentType = entry.sys.contentType.sys.id;
        // const slug = entry.fields.slug;

        // let href = "/"; // URL par défaut

        // // Définir les URL en fonction du contentType (personnaliser selon tes besoins)
        // if (contentType === "blogPost") {
        //   href = `/blog/${slug}`;
        // } else if (contentType === "product") {
        //   href = `/products/${slug}`;
        // }

        return (
          <Link className="underline" href={link()}>
            {children}
          </Link>
        );
      },
      [INLINES.HYPERLINK]: (node: any, children: any) => {
        const url = node.data.uri;
        const isExternalLink = url.startsWith("http");

        if (isExternalLink) {
          return (
            <a
              href={url}
              className="underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              {children}
            </a>
          );
        }

        // Gérer les liens internes si nécessaire
        return (
          <a href={url} className="underline">
            {children}
          </a>
        );
      },
      [BLOCKS.EMBEDDED_ENTRY]: (node: any) => {
        const entry = findBlockDetails(node.data.target.sys.id);

        // return <pre>{JSON.stringify([node, entry], null, 2)}</pre>;
        return entry.__typename === "Video" ? (
          <YouTubePlayer url={entry.youtubeUrl} />
        ) : entry.__typename === "Profile" ? (
          // <YouTubePlayer url={entry.youtubeUrl} />
          // <pre>{JSON.stringify(entry, null, 2)}</pre>
          <div className="mb-4">
            <ProfileSmallCard entry={entry} />
          </div>
        ) : entry.__typename === "Calendar" ? (
          <Timeline dates={entry.calendarData.list} />
        ) : // <YouTubePlayer url={entry.youtubeUrl} />
        // <pre>{JSON.stringify(entry, null, 2)}</pre>
        null;
      },
    },
  };

  return documentToReactComponents(richTextDocument, options);
};
